import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import {
  generatePath,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import WorkoutContentStore from "stores/WorkoutContentStore";
import { Page } from "components/Layout";
import WorkoutsArea from "components/Workout/Workouts";
import NavList from "pages/WorkoutMain/NavList";
import { capitalize } from "utils/common";
import ROUTES from "navigation/routes";
import { WEEK } from "constants/common";
import { CONTENT_STORE_TYPE } from "constants/workout";
import { withTranslation, WithTranslation } from "react-i18next";

import { Background, Wrapper } from "./styles";

interface LocationState {
  id?: number;
  title?: string;
}

type RouteParams = {
  categoryId: string;
  workoutGroup: string;
};

type Props = WithTranslation;

const Workouts: React.FC<Props> = observer(props => {
  const navigate = useNavigate();
  const params = useParams<RouteParams>();
  const location = useLocation();

  const [store] = React.useState<Instance<typeof WorkoutContentStore> | null>(
    null,
  );

  const url = location.pathname;

  const categoryId = Number.parseInt(params.categoryId || "");
  const workoutGroup = params.workoutGroup || "";

  const isFavorites = !categoryId;

  const isWeekCategory = Object.values(WEEK).includes(workoutGroup);

  const dayOfWeek = Object.keys(WEEK).find(key => WEEK[key] === workoutGroup);

  const workoutGroupIsNewOrAll = [CONTENT_STORE_TYPE.STANDARD_ALL].includes(
    workoutGroup,
  );

  const workoutGroupIsValid =
    (isWeekCategory && dayOfWeek !== undefined) ||
    isFavorites ||
    workoutGroupIsNewOrAll;

  const locState = location.state as LocationState;
  let title: string;
  if (workoutGroup === CONTENT_STORE_TYPE.STANDARD_ALL) {
    title = locState?.title ?? location.state.categoryTitle ?? capitalize(workoutGroup);
  } else if (isFavorites) {
    title = store?.title ?? capitalize(workoutGroup);
  } else {
    title = capitalize(workoutGroup);
  }

  useEffect(() => {
    if (!workoutGroupIsValid) {
      navigate(generatePath(ROUTES.WORKOUT), { replace: true });
    }
  }, [workoutGroupIsValid, navigate]);

  const onNavigateToWorkout =
    (workoutGroupParam?: string) => (workout: any) => {
      const { id: workoutId } = workout;

      navigate(
        generatePath(
          isFavorites
            ? ROUTES.WORKOUT_FAVORITES_WORKOUT_DETAILS
            : ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS_WORKOUT_DETAILS,
          {
            categoryId,
            workoutGroup: workoutGroupParam || workoutGroup,
            workoutId,
          },
        ),
        { state: { categoryId: categoryId, categoryTitle: title, id: workoutId, familyId: location.state?.familyId, familyTitle: location.state?.familyTitle } },
      );
    };

  const onNavigateToWorkoutMain = () => {
    navigate(generatePath(ROUTES.WORKOUT));
  };

  const getTranslatedTitle = (title: string): string => {
    const t: (key: string) => string = props.t;
    if (isWeekCategory) {
      const key = title?.toLowerCase();
      return t(`weekdays.${key}`);
    } else {
      return title;
    }
  };

  return (
    <Page
      isLoading={store?.isLoading}
      title={getTranslatedTitle(title)}
      contentBackground={<Background />}
      topNavigation={<NavList />}
    >
      <Wrapper>
        <WorkoutsArea
          categoryId={categoryId}
          workoutGroup={workoutGroup}
          onNavigateToWorkout={onNavigateToWorkout}
          onNavigateToWorkoutMain={onNavigateToWorkoutMain}
          url={url}
          history={undefined}
        />
      </Wrapper>
    </Page>
  );
});

export default withTranslation()(Workouts);
